import { useEffect, useMemo, useRef, useState } from "react";
import { loadGoogleMaps } from "../../utils/googleMapsLoader";
import { IDriverActiveOrder } from "../../models/IDriver";
import { DriverPosition } from "../hooks/useDriverLocationSharing";
import { LatLng, MOBILE_MAX_WAYPOINTS, RouteStop, mapsMultiStopUrl, planRoute } from "../routeUtils";
import { collectsAtDoor, formatMoney, formatTime, mapsDirectionsUrl, telHref } from "../utils";

interface Props {
  orders: IDriverActiveOrder[];
  currentPosition: DriverPosition | null;
  onClose: () => void;
}

interface Leg { distance: string; meters: number; duration: string; seconds: number }

const PICKUP_COLOR = "#4f46e5";
const DROPOFF_COLOR = "#ea580c";

export default function RoutePlannerModal({ orders, currentPosition, onClose }: Props) {
  const [origin, setOrigin] = useState<LatLng | null>(currentPosition);
  const [locating, setLocating] = useState(!currentPosition);
  const [recalcKey, setRecalcKey] = useState(0);

  const [ordered, setOrdered] = useState<RouteStop[]>([]);
  const [legs, setLegs] = useState<Leg[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [startedAt, setStartedAt] = useState(Date.now());

  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const youMarkerRef = useRef<google.maps.Marker | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const rendererRef = useRef<google.maps.DirectionsRenderer | null>(null);

  // Konum yoksa bir kez sor
  useEffect(() => {
    if (origin || !navigator.geolocation) { setLocating(false); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Konum alınamazsa ilk restorandan / ilk teslimattan başla (sıralama yine anlamlı olsun)
  const start: LatLng | null = useMemo(() => {
    if (origin) return origin;
    const o = orders.find((x) => x.restaurant.latitude != null && x.restaurant.longitude != null);
    return o ? { lat: o.restaurant.latitude!, lng: o.restaurant.longitude! } : null;
  }, [origin, orders]);

  const plan = useMemo(() => (start ? planRoute(start, orders) : null), [start, orders]);

  // Haritayı kur ve rotayı çiz
  useEffect(() => {
    if (locating || !start || !plan || !plan.stops.length) { if (!locating) setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError("");

    loadGoogleMaps().then(() => {
      if (cancelled || !mapDivRef.current) return;
      if (!mapRef.current) {
        mapRef.current = new google.maps.Map(mapDivRef.current, {
          center: start, zoom: 13, disableDefaultUI: true, zoomControl: true, clickableIcons: false,
        });
        rendererRef.current = new google.maps.DirectionsRenderer({
          map: mapRef.current, suppressMarkers: true,
          polylineOptions: { strokeColor: DROPOFF_COLOR, strokeWeight: 5, strokeOpacity: 0.9 },
        });
      }
      const map = mapRef.current;
      const stops = plan.stops;
      const destination = stops[stops.length - 1];
      const middle = stops.slice(0, -1);

      new google.maps.DirectionsService().route({
        origin: start,
        destination: { lat: destination.lat, lng: destination.lng },
        waypoints: middle.map((s) => ({ location: { lat: s.lat, lng: s.lng }, stopover: true })),
        // Alınacak restoran yoksa ara teslimatların sırasını Google gerçek yol süresine göre iyileştirsin.
        // Restoran varsa sıra sabit: teslimat, siparişi almadan önce gelemez.
        optimizeWaypoints: !plan.hasPickups && middle.length > 1,
        travelMode: google.maps.TravelMode.DRIVING,
      }, (result, status) => {
        if (cancelled) return;
        if (status !== "OK" || !result) {
          setError("Rota hesaplanamadı. Duraklar kuş uçuşu yakınlığa göre sıralandı.");
          setOrdered(stops);
          setLegs([]);
          drawMarkers(map, stops);
          setLoading(false);
          return;
        }
        rendererRef.current?.setDirections(result);
        const route = result.routes[0];
        const order = route.waypoint_order?.length === middle.length ? route.waypoint_order : middle.map((_, i) => i);
        const finalStops = [...order.map((i) => middle[i]), destination];
        setOrdered(finalStops);
        setLegs(route.legs.map((l) => ({
          distance: l.distance?.text ?? "", meters: l.distance?.value ?? 0,
          duration: l.duration?.text ?? "", seconds: l.duration?.value ?? 0,
        })));
        setStartedAt(Date.now());
        drawMarkers(map, finalStops);
        setLoading(false);
      });

      // Kuryenin kendisi
      if (!youMarkerRef.current && origin) {
        youMarkerRef.current = new google.maps.Marker({
          position: origin, map, zIndex: 999,
          icon: { path: google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#2563eb", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3 },
        });
      }
    }).catch(() => {
      if (!cancelled) { setError("Harita yüklenemedi."); setLoading(false); }
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locating, plan, recalcKey]);

  // Canlı konum: yalnız işaret hareket eder, rota "Yeniden hesapla" ile güncellenir
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (pos) => youMarkerRef.current?.setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  const drawMarkers = (map: google.maps.Map, stops: RouteStop[]) => {
    markersRef.current.forEach((m) => m.setMap(null));
    const bounds = new google.maps.LatLngBounds();
    if (origin) bounds.extend(origin);
    markersRef.current = stops.map((s, i) => {
      bounds.extend({ lat: s.lat, lng: s.lng });
      return new google.maps.Marker({
        position: { lat: s.lat, lng: s.lng }, map, zIndex: 500 - i,
        title: `${i + 1}. ${s.kind === "pickup" ? "Al" : "Teslim"}: ${s.title}`,
        label: { text: String(i + 1), color: "#fff", fontWeight: "800", fontSize: "13px" },
        icon: {
          path: google.maps.SymbolPath.CIRCLE, scale: 14,
          fillColor: s.kind === "pickup" ? PICKUP_COLOR : DROPOFF_COLOR, fillOpacity: 1,
          strokeColor: "#fff", strokeWeight: 3,
        },
      });
    });
    map.fitBounds(bounds, 60);
  };

  const recalc = () => {
    // Güncel konumu alıp sırayı baştan kur
    if (!navigator.geolocation) { setRecalcKey((k) => k + 1); return; }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { youMarkerRef.current?.setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setRecalcKey((k) => k + 1); },
      () => setRecalcKey((k) => k + 1),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
    );
  };

  const totals = legs.reduce((acc, l) => ({ seconds: acc.seconds + l.seconds }), { seconds: 0 });
  const totalKm = legs.reduce((sum, l) => sum + l.meters, 0) / 1000;

  // Google Maps telefonda en çok 3 ara durak alır: rota uzunsa ilk 4 durak açılır, kalanı sonra
  const navStops = ordered.slice(0, MOBILE_MAX_WAYPOINTS + 1);
  const navUrl = navStops.length ? mapsMultiStopUrl(navStops) : "";
  const truncated = ordered.length > navStops.length;
  const missing = plan?.missing ?? [];

  let cumulative = 0;

  return (
    <div className="dp-map-wrap" onClick={onClose}>
      <div className="dp-map" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="dp-map__head">
          <div className="dp-map__icon" style={{ background: "#fff7ed", color: DROPOFF_COLOR }}>
            <i className="bi bi-signpost-split-fill"></i>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="dp-map__title">Toplu rota · {orders.length} sipariş</div>
            <div className="dp-map__sub">
              {legs.length
                ? <>{ordered.length} durak · {totalKm ? `${totalKm.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} km · ` : ""}≈ {Math.round(totals.seconds / 60)} dk</>
                : "En kısa sıra hesaplanıyor…"}
            </div>
          </div>
          <button className="dp-icon-btn" onClick={recalc} aria-label="Konumuma göre yeniden hesapla" title="Konumuma göre yeniden hesapla" disabled={loading}>
            <i className="bi bi-arrow-repeat"></i>
          </button>
          <button className="dp-icon-btn" onClick={onClose} aria-label="Kapat">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="dp-route">
          <div className="dp-route__map">
            {locating ? (
              <div className="dp-center" style={{ paddingTop: 60 }}>
                <div className="spinner-border" style={{ color: DROPOFF_COLOR }} role="status" />
                <div style={{ marginTop: 12, fontSize: ".85rem", color: "#6b7280" }}>Konumunuz alınıyor...</div>
              </div>
            ) : (
              <>
                <div ref={mapDivRef} style={{ width: "100%", height: "100%" }} />
                {loading && (
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,.6)" }}>
                    <div className="spinner-border" style={{ color: DROPOFF_COLOR }} role="status" />
                  </div>
                )}
                <div className="dp-route__legend">
                  <span><i style={{ background: PICKUP_COLOR }} />Al</span>
                  <span><i style={{ background: DROPOFF_COLOR }} />Teslim et</span>
                  <span><i style={{ background: "#2563eb" }} />Sen</span>
                </div>
              </>
            )}
          </div>

          <div className="dp-route__list">
            {!origin && !locating && (
              <div className="dp-alert dp-alert--warn" style={{ marginBottom: 10 }}>
                <i className="bi bi-geo-alt"></i><span>Konumun alınamadı; sıra restorandan başlanarak hesaplandı.</span>
              </div>
            )}
            {error && (
              <div className="dp-alert dp-alert--warn" style={{ marginBottom: 10 }}>
                <i className="bi bi-exclamation-triangle-fill"></i><span>{error}</span>
              </div>
            )}
            {missing.length > 0 && (
              <div className="dp-alert dp-alert--warn" style={{ marginBottom: 10 }}>
                <i className="bi bi-question-circle-fill"></i>
                <span>Konumu olmadığı için rotaya eklenemedi: {missing.map((m) => `#${m.orderNameUniqe ?? "—"}`).join(", ")}</span>
              </div>
            )}

            {ordered.map((s, i) => {
              const leg = legs[i];
              if (leg) cumulative += leg.seconds;
              const eta = leg ? formatTime(new Date(startedAt + cumulative * 1000)) : null;
              const isPickup = s.kind === "pickup";
              const order = s.orders[0];
              const cash = !isPickup && order && collectsAtDoor(order);
              return (
                <div key={s.key} className={`dp-route-stop${i === 0 ? " is-next" : ""}`}>
                  <div className="dp-route-stop__no" style={{ background: isPickup ? PICKUP_COLOR : DROPOFF_COLOR }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="dp-route-stop__kind" style={{ color: isPickup ? PICKUP_COLOR : DROPOFF_COLOR }}>
                      {isPickup ? `Restorandan al · ${s.orders.length} sipariş` : `Teslim et · #${order?.orderNameUniqe ?? ""}`}
                      {i === 0 && <span className="dp-stop__now" style={{ marginLeft: 6 }}>SIRADAKİ</span>}
                    </div>
                    <div className="dp-route-stop__title">{s.title}</div>
                    {s.address && <div className="dp-route-stop__addr">{s.address}</div>}
                    <div className="dp-route-stop__meta">
                      {leg && <span><i className="bi bi-signpost-2"></i>{leg.distance} · {leg.duration}</span>}
                      {eta && <span><i className="bi bi-clock"></i>≈ {eta}</span>}
                      {cash && <span style={{ color: "#b45309", fontWeight: 700 }}><i className="bi bi-cash-coin"></i>{formatMoney(order.orderPrice, order.currencyDisplayText)} tahsil</span>}
                    </div>
                  </div>
                  <div className="dp-route-stop__actions">
                    <a className="dp-icon-btn" href={mapsDirectionsUrl(s.lat, s.lng)} target="_blank" rel="noopener noreferrer" title="Bu durağa navigasyon" aria-label="Bu durağa navigasyon">
                      <i className="bi bi-cursor-fill"></i>
                    </a>
                    {s.phone && (
                      <a className="dp-icon-btn" href={telHref(s.phone)} title="Ara" aria-label="Ara">
                        <i className="bi bi-telephone-fill"></i>
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
            {ordered.length > 0 && legs.length > 0 && (
              <div className="dp-small dp-muted" style={{ fontSize: ".72rem", color: "#9ca3af", marginTop: 6 }}>
                Varış saatleri trafiğe göre tahminidir; durakta bekleme süresi dahil değildir.
              </div>
            )}
          </div>
        </div>

        <div className="dp-map__foot">
          {navUrl ? (
            <a className="dp-btn dp-btn--primary" href={navUrl} target="_blank" rel="noopener noreferrer">
              <i className="bi bi-cursor-fill"></i> {truncated ? `İlk ${navStops.length} durakla navigasyonu başlat` : "Rotayı Google Maps'te başlat"}
            </a>
          ) : (
            <button className="dp-btn dp-btn--ghost" onClick={onClose}>Kapat</button>
          )}
        </div>
        {truncated && (
          <div style={{ padding: "0 16px 12px", fontSize: ".74rem", color: "#92400e", background: "#fff" }}>
            <i className="bi bi-info-circle me-1"></i>Google Maps telefonda en çok {MOBILE_MAX_WAYPOINTS} ara durak alıyor. Kalan duraklar için listedeki <i className="bi bi-cursor-fill"></i> butonunu kullan.
          </div>
        )}
      </div>
    </div>
  );
}
